import SizeChips from "./SizeChips";
import { money, relativeTime } from "../lib/format";
import type { Product } from "../lib/types";

export interface HangtagProps {
  product: Product;
  /** True when the viewer has a watch on this product at all. */
  watched?: boolean;
  /** Sizes inside that watch; empty means "any size". */
  watchedSizes?: string[];
  /** Omitted for anonymous visitors — the watch controls then disappear. */
  onToggleWatch?: () => void;
  onToggleSize?: (size: string) => void;
  /** Drops the marketing copy, for dense listings. */
  compact?: boolean;
  /** Where the title links. Omitted renders plain text. */
  href?: string;
}

function priceLabel(product: Product): string {
  const { priceCents, priceMinCents, priceMaxCents } = product;
  if (
    Number.isFinite(priceMinCents) &&
    Number.isFinite(priceMaxCents) &&
    priceMaxCents > priceMinCents
  ) {
    return `${money(priceMinCents)} – ${money(priceMaxCents)}`;
  }
  return money(priceCents);
}

/** Unique, non-blank description lines, so the line itself is a safe React key. */
function descriptionLines(product: Product): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const line of product.descriptionLines ?? []) {
    if (typeof line !== "string") continue;
    const text = line.trim();
    if (text === "" || seen.has(text)) continue;
    seen.add(text);
    out.push(text);
  }
  return out;
}

/**
 * The store prints the whole size ladder and strikes through what is gone, so
 * the tag needs every size, not just the buyable ones. `/api/shop` sends
 * `allSizes` for exactly this; `availableSizes` then says which of them are
 * still live. Falling back to `availableSizes` alone would render an empty size
 * row for any sold-out product — which, on this store, is most of them.
 */
function resolveSizes(product: Product): string[] {
  if (product.allSizes?.length) return product.allSizes;
  return product.availableSizes ?? [];
}

/** "restocked 3x · last seen 2h ago" — the line the printed tag has no reason to carry. */
function watcherLine(product: Product): string {
  const parts: string[] = [];
  if (product.restockCount > 0) parts.push(`restocked ${product.restockCount}x`);
  if (product.lastSeen) parts.push(`last seen ${relativeTime(product.lastSeen)}`);
  return parts.join(" · ");
}

/**
 * The white tag that hangs beside every product: price, title, copy, sizes,
 * sold out, then the watcher's own controls.
 *
 * The store's tag also carries a size-guide link, a signature mark and a
 * returns line. None of those are reproduced here — the first two point at
 * nothing in this app, and the third is the store's policy to state, not ours.
 */
export default function Hangtag({
  product,
  watched = false,
  watchedSizes,
  onToggleWatch,
  onToggleSize,
  compact = false,
  href,
}: HangtagProps) {
  const sizes = resolveSizes(product);
  const lines = compact ? [] : descriptionLines(product);
  const meta = watcherLine(product);

  return (
    <div className={`ap-tag${compact ? " ap-tag--compact" : ""}`}>
      <p className="ap-tag__price">{priceLabel(product)}</p>

      <h2 className="ap-tag__title">
        {href ? (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {product.title}
          </a>
        ) : (
          product.title
        )}
      </h2>

      {lines.length > 0 && (
        <div className="ap-tag__desc">
          {lines.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      )}

      {sizes.length > 0 ? (
        <SizeChips
          sizes={sizes}
          availableSizes={product.availableSizes}
          watchedSizes={watchedSizes}
          onToggle={onToggleSize}
        />
      ) : (
        product.totalVariants > 0 && (
          <p className="ap-muted">
            {product.availableVariants} of {product.totalVariants} sizes available
          </p>
        )
      )}

      {!product.available && <p className="ap-tag__soldout">SOLD OUT</p>}

      {onToggleWatch && (
        <button
          type="button"
          className={`ap-btn ap-btn--sm${watched ? " ap-btn--on" : ""}`}
          aria-pressed={watched}
          onClick={onToggleWatch}
        >
          {watched ? "WATCHING" : "WATCH"}
          <span className="ap-visually-hidden"> {product.title}</span>
        </button>
      )}

      {meta && <p className="ap-muted">{meta}</p>}
    </div>
  );
}
